/* eslint-disable no-unused-vars */
import { initializeApp } from 'firebase/app'
import { getAuth, signInWithCustomToken } from 'firebase/auth'
import { getDatabase } from 'firebase/database'
import {
  getStorage,
  ref,
  uploadBytesResumable,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage'
import { v4 as uuidv4 } from 'uuid'
import apiFirebaseAuth from '../features/auth/services/firebaseAuth'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE,
  databaseURL: import.meta.env.VITE_FIREBASE_REALTIME_DATABASE,
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)
const storage = getStorage(app)
const auth = getAuth(app)

export const database = getDatabase(app)

export const storageApi = {}

storageApi.interviewUpload = async (file, interviewVersionId, questionId) => {
  const fileExtension = file.name.split('.').at(-1)
  const fileUrl = `interview-versions/${interviewVersionId}/Q-${questionId}.${fileExtension}`
  const fileRef = ref(storage, fileUrl)
  await uploadBytes(fileRef, file)
  return fileUrl
}

const generateFileId = () => uuidv4().replace(/-/g, '').substring(0, 27)

storageApi.uploadFile = (
  file,
  onUpload,
  onSuccess,
  onError,
  directory = null,
) => {
  const fileExtension = file.name.split('.').at(-1)
  let fileUrl = ''
  if (directory) {
    fileUrl = `resumes/${directory}/${file.name}`
  } else {
    fileUrl = `resumes/${generateFileId()}.${fileExtension}`
  }
  const fileRef = ref(storage, fileUrl)

  const uploadTask = uploadBytesResumable(fileRef, file)

  uploadTask.on(
    'state_changed',
    (snapshot) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
      onUpload(Math.ceil(progress))
    },
    (error) => {
      let errorMessage = ''
      switch (error.code) {
        case 'storage/unauthorized':
          errorMessage = 'you can only upload pdf with size less than 5 MB'
          break
        case 'storage/canceled':
          errorMessage = 'the upload has been canceled'
          break
        default:
          errorMessage = 'something went wrong please try again later'
      }
      if (onError) onError(errorMessage)
    },
    () => {
      if (onSuccess) onSuccess(fileUrl)
    },
  )
  return uploadTask.then()
}

storageApi.uploadCompanyLogo = (
  file,
  fileName,
  onUpload,
  onSuccess,
  onError,
) => {
  const fileExtension = file.name.split('.').at(-1)
  const fileUrl = `logos/${fileName}.${fileExtension}`
  const fileRef = ref(storage, fileUrl)

  const uploadTask = uploadBytesResumable(fileRef, file)
  uploadTask.on(
    'state_changed',
    (snapshot) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
      onUpload(Math.ceil(progress))
    },
    (error) => {
      let errorMessage = ''
      switch (error.code) {
        case 'storage/unauthorized':
          errorMessage = 'you can only upload image with size less than 5 MB'
          break
        case 'storage/canceled':
          errorMessage = 'the upload has been canceled'
          break
        default:
          errorMessage = 'something went wrong please try again later'
      }
      if (onError) onError(errorMessage)
    },
    async () => {
      const downloadURL = await getDownloadURL(fileRef)
      if (onSuccess) onSuccess(downloadURL)
    },
  )
  return uploadTask.then()
}

storageApi.downloadDatabaseTemplateFile = async (filePath) => {
  try {
    const fileRef = ref(storage, filePath)
    const downloadUrl = await getDownloadURL(fileRef)

    window.open(downloadUrl, '_blank')

    console.log('File download successful!')
  } catch (error) {
    console.error('Error downloading file:', error)
  }
}

storageApi.uploadVideo = (file, companyId, onProgress, onSuccess, onError) => {
  const fileExtension = file.name.split('.').at(-1)
  const fileUrl = `trainings/${companyId}/${uuidv4()}.${fileExtension}`
  const fileRef = ref(storage, fileUrl)

  const uploadTask = uploadBytesResumable(fileRef, file)

  uploadTask.on(
    'state_changed',
    (snapshot) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
      if (onProgress) onProgress(Math.ceil(progress))
    },
    (error) => {
      if (onError) onError(error.message || 'Upload failed')
    },
    () => {
      if (onSuccess) onSuccess(fileUrl)
    },
  )
  return uploadTask.then()
}

storageApi.uploadEmailAttachment = async (
  file,
  emailId,
  onUpload,
  onSuccess,
  onError,
) => {
  try {
    const fileExtension = file.name.split('.').at(-1)
    const fileUrl = `email-attachments/${emailId}/${uuidv4()}.${fileExtension}`
    const fileRef = ref(storage, fileUrl)

    const uploadTask = uploadBytesResumable(fileRef, file)

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          onUpload(Math.ceil(progress))
        },
        (error) => {
          let errorMessage = ''
          switch (error.code) {
            case 'storage/unauthorized':
              errorMessage =
                'You can only upload attachments with size less than 10 MB'
              break
            case 'storage/canceled':
              errorMessage = 'The upload has been canceled'
              break
            default:
              errorMessage = 'Something went wrong, please try again later'
          }
          if (onError) onError(errorMessage)
          reject(new Error(errorMessage))
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(fileRef)
            if (onSuccess) onSuccess(downloadURL)
            resolve(downloadURL)
          } catch (error) {
            if (onError) onError(`Failed to get download URL: ${error.message}`)
            reject(new Error(`Failed to get download URL: ${error.message}`))
          }
        },
      )
    })
  } catch (error) {
    if (onError) onError(`Failed to upload attachment: ${error.message}`)
    return Promise.reject(
      new Error(`Failed to upload attachment: ${error.message}`),
    )
  }
}

const uid = 'talentino_user'

apiFirebaseAuth.getCustomToken(uid).then((customToken) => {
  signInWithCustomToken(auth, customToken)
    .catch((error) => {
      console.error('Error signing in with custom token:', error)
    })
})
